// The Markdown a post is written in (site/content/README.md): headings, paragraphs, lists,
// pull quotes with an attribution, callouts, figures, code, tables and rules, with inline code,
// bold, italics and links. Adapted from the admin web's parser (web/src/ui/markdown/parse.ts);
// parsed into plain data that `Markdown` renders as React elements, so no text is ever injected
// as HTML.

export type Inline =
  | { kind: "text"; text: string }
  | { kind: "code"; text: string }
  | { kind: "strong"; children: Inline[] }
  | { kind: "em"; children: Inline[] }
  | { kind: "link"; text: string; href: string };

export interface ListItem {
  text: Inline[];
  children?: Extract<Block, { kind: "list" }>;
}

export type CalloutTone = "note" | "tip" | "warning";

export type Block =
  | { kind: "heading"; level: 2 | 3 | 4; text: Inline[]; id: string }
  | { kind: "paragraph"; text: Inline[] }
  | { kind: "list"; ordered: boolean; items: ListItem[] }
  | { kind: "quote"; paragraphs: Inline[][]; cite?: Inline[] }
  | { kind: "callout"; tone: CalloutTone; paragraphs: Inline[][] }
  | { kind: "figure"; src: string; alt: string; caption: string }
  | { kind: "code"; lang: string; text: string }
  | { kind: "table"; head: Inline[][]; rows: Inline[][][] }
  | { kind: "rule" };

const INLINE = /(`[^`]+`)|(\*\*(?:[^*]|\*(?!\*))+\*\*)|(\*[^*\s][^*]*\*|_[^_\s][^_]*_)|(\[[^\]]+\]\([^)\s]+\))/g;

/** Links a reader may follow: web pages, email and the site's own paths, never `javascript:`. */
const SAFE_HREF = /^(https?:\/\/|mailto:|\/(?!\/)|#)/i;

export function inline(text: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  for (const m of text.matchAll(INLINE)) {
    const at = m.index;
    if (at > last) out.push({ kind: "text", text: text.slice(last, at) });
    const token = m[0];
    if (m[1]) out.push({ kind: "code", text: token.slice(1, -1) });
    else if (m[2]) out.push({ kind: "strong", children: inline(token.slice(2, -2)) });
    else if (m[3]) out.push({ kind: "em", children: inline(token.slice(1, -1)) });
    else {
      const close = token.indexOf("](");
      const href = token.slice(close + 2, -1);
      const label = token.slice(1, close);
      out.push(SAFE_HREF.test(href) ? { kind: "link", text: label, href } : { kind: "text", text: label });
    }
    last = at + token.length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

/** The anchor a heading gets: "What's in your control?" → "whats-in-your-control". */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[’'"*`]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

const LIST_ITEM = /^(\s*)(?:([-*+])|(\d+)[.)])\s+(.*)$/;
const TABLE_RULE = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const FIGURE = /^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)\s*$/;
const CALLOUT = /^\[!(NOTE|TIP|WARNING)\]\s*$/i;
const CITE = /^(—|--)\s*/;
const indentOf = (s: string): number => s.replace(/\t/g, "    ").length;
const startsBlock = (l: string): boolean => /^(#{1,6}\s|```|>)/.test(l) || LIST_ITEM.test(l) || RULE.test(l) || FIGURE.test(l.trim());

function list(lines: string[], start: number, indent: number): { block: Extract<Block, { kind: "list" }>; next: number } {
  const ordered = LIST_ITEM.exec(lines[start] ?? "")?.[3] !== undefined;
  const items: ListItem[] = [];
  let i = start;
  while (i < lines.length) {
    const m = LIST_ITEM.exec(lines[i] ?? "");
    if (!m) break;
    const depth = indentOf(m[1] ?? "");
    if (depth < indent) break;
    if (depth > indent) {
      const last = items.at(-1);
      if (!last) break;
      const nested = list(lines, i, depth);
      last.children = nested.block;
      i = nested.next;
      continue;
    }
    if ((m[3] !== undefined) !== ordered) break;
    items.push({ text: inline(m[4] ?? "") });
    i++;
  }
  return { block: { kind: "list", ordered, items }, next: i };
}

const cells = (row: string): Inline[][] =>
  row
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => inline(c.trim()));

/** The `>` lines from `i`, without their markers. */
function quoteLines(lines: string[], i: number): { body: string[]; next: number } {
  const body: string[] = [];
  while (i < lines.length && (lines[i] ?? "").startsWith(">")) body.push((lines[i++] ?? "").replace(/^>\s?/, ""));
  return { body, next: i };
}

/** Paragraphs separated by blank lines, each joined into one line. */
function paragraphs(body: string[]): string[] {
  const out: string[][] = [[]];
  for (const l of body) {
    if (l.trim() === "") out.push([]);
    else out.at(-1)!.push(l.trim());
  }
  return out.filter((p) => p.length > 0).map((p) => p.join(" "));
}

function quoteBlock(body: string[]): Block {
  const callout = CALLOUT.exec(body[0]?.trim() ?? "");
  if (callout) {
    return { kind: "callout", tone: callout[1]!.toLowerCase() as CalloutTone, paragraphs: paragraphs(body.slice(1)).map(inline) };
  }
  const citeAt = body.findIndex((l) => CITE.test(l.trim()));
  const cite = citeAt >= 0 ? inline(body[citeAt]!.trim().replace(CITE, "")) : undefined;
  const text = citeAt >= 0 ? body.filter((_, j) => j !== citeAt) : body;
  return { kind: "quote", paragraphs: paragraphs(text).map(inline), cite };
}

export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const fence = /^```(.*)$/.exec(line.trim());
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").trim().startsWith("```")) body.push(lines[i++] ?? "");
      i++;
      blocks.push({ kind: "code", lang: (fence[1] ?? "").trim(), text: body.join("\n") });
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      // The post's title is its only h1; deeper levels clamp at h4.
      const level = Math.min(4, Math.max(2, (heading[1] ?? "#").length)) as 2 | 3 | 4;
      const text = (heading[2] ?? "").trim();
      blocks.push({ kind: "heading", level, text: inline(text), id: slugify(text) });
      i++;
      continue;
    }
    if (RULE.test(line)) {
      blocks.push({ kind: "rule" });
      i++;
      continue;
    }
    const figure = FIGURE.exec(line.trim());
    if (figure) {
      blocks.push({ kind: "figure", alt: figure[1] ?? "", src: figure[2] ?? "", caption: figure[3] ?? "" });
      i++;
      continue;
    }
    if (line.startsWith(">")) {
      const quote = quoteLines(lines, i);
      blocks.push(quoteBlock(quote.body));
      i = quote.next;
      continue;
    }
    const item = LIST_ITEM.exec(line);
    if (item) {
      const parsed = list(lines, i, indentOf(item[1] ?? ""));
      blocks.push(parsed.block);
      i = parsed.next;
      continue;
    }
    if (line.includes("|") && TABLE_RULE.test(lines[i + 1] ?? "")) {
      const rows: Inline[][][] = [];
      i += 2;
      while (i < lines.length && (lines[i] ?? "").includes("|") && (lines[i] ?? "").trim() !== "") rows.push(cells(lines[i++] ?? ""));
      blocks.push({ kind: "table", head: cells(line), rows });
      continue;
    }
    if (line.trim() === "") {
      i++;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length) {
      const l = lines[i] ?? "";
      if (l.trim() === "" || (para.length > 0 && startsBlock(l))) break;
      para.push(l.trim());
      i++;
    }
    blocks.push({ kind: "paragraph", text: inline(para.join(" ")) });
  }
  return blocks;
}
